import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from 'store';

import searchBy from 'shared/utils/searchBy';
import formatDate from 'shared/utils/formatDate';
import processTableFilterOptions from 'shared/utils/processTableFilterOptions';

import { cooperativeCommonCasesApi } from '../api';
import {
  getUniqueCaseLevel1List,
  getUniqueStatusesList,
  getUniqueYearsList,
} from '../lib';
import type { CooperativeCommonCase } from '../models';

const selectSelf = (state: RootState) => state;

const emptyCooperativeCommonCasesRawList: CooperativeCommonCase[] = [];

export const selectCooperativeCommonCasesRawList = createSelector(
  [
    selectSelf,
    (_, params: { cooperativeId: string | null; isConstructor: boolean }) =>
      params,
  ],
  (state, { cooperativeId, isConstructor }) => {
    if (!cooperativeId) return emptyCooperativeCommonCasesRawList;

    return (
      cooperativeCommonCasesApi.endpoints.getCooperativeCommonCasesList.select({
        cooperativeId,
        isConstructor,
      })(state).data ?? emptyCooperativeCommonCasesRawList
    );
  }
);

export const selectUniqueStatusesList = createSelector(
  selectCooperativeCommonCasesRawList,
  (list) => processTableFilterOptions(getUniqueStatusesList(list))
);

export const selectUniqueCaseLevel1List = createSelector(
  selectCooperativeCommonCasesRawList,
  (list) => processTableFilterOptions(getUniqueCaseLevel1List(list))
);

export const selectUniqueYearsList = createSelector(
  selectCooperativeCommonCasesRawList,
  (list) =>
    processTableFilterOptions(getUniqueYearsList(list), { sortOrder: 'desc' })
);

export const selectSearchTerm = createSelector(
  selectSelf,
  (state) => state.cooperativeCommonCases.searchTerm
);

export const selectFilteredCasesBySearchTerm = createSelector(
  selectCooperativeCommonCasesRawList,
  selectSearchTerm,
  (list, searchTerm) =>
    searchBy(
      list,
      [
        (caseItem) => caseItem.Title ?? '',
        (caseItem) => caseItem.Space?.Name ?? '',
        (caseItem) => caseItem.CaseLevel1?.Label ?? '',
        (caseItem) => caseItem.Status?.Label ?? '',
        (caseItem) => caseItem.Description ?? '',
        (caseItem) =>
          caseItem.CreatedOn ? formatDate(new Date(caseItem.CreatedOn)) : '',
        (caseItem) =>
          caseItem.ModifiedOn ? formatDate(new Date(caseItem.ModifiedOn)) : '',
      ],
      searchTerm
    )
);
